import { DocumentManager, MAX_RECENT_FILES } from "../doc/DocumentManager.js";
import { CMD, FILE_RECENT_SLOT_IDS } from "./commandIds.js";
import { languageLabel } from "../doc/languageMode.js";
import { LANGUAGE_MENU_ROWS } from "./languageMenuItems.js";
import type { MenuEntry, TopLevelMenu } from "./menuTypes.js";

/**
 * Canonical top-level order for accordance tests — matches classic Notepad++ menubar.
 * @see PROJECT_SOURCE_CODE_DESCRIPTION.md §16–17
 */
export const NOTEPAD_TOP_MENU_LABELS = [
  "File",
  "Edit",
  "Search",
  "View",
  "Encoding",
  "Language",
  "Settings",
  "Tools",
  "?",
] as const;

export type NotepadTopMenuLabel = (typeof NOTEPAD_TOP_MENU_LABELS)[number];

const FILE_MENU_BEFORE_RECENT: MenuEntry[] = [
  { type: "item", id: CMD.FILE_NEW, label: "New", shortcut: "Ctrl+N" },
  { type: "item", id: CMD.FILE_OPEN, label: "Open… (text, image, or PDF)", shortcut: "Ctrl+O" },
  { type: "item", id: CMD.FILE_OPEN_FOLDER, label: "Open Folder as Workspace…", shortcut: "Ctrl+Shift+O" },
  { type: "item", id: CMD.FILE_OPEN_HTML_URL, label: "Open HTML from URL…" },
  { type: "item", id: CMD.FILE_CLOSE, label: "Close", shortcut: "Ctrl+W" },
  { type: "item", id: CMD.FILE_CLOSEALL, label: "Close All" },
  { type: "separator" },
  { type: "item", id: CMD.FILE_SAVE, label: "Save", shortcut: "Ctrl+S" },
  { type: "item", id: CMD.FILE_SAVEAS, label: "Save As…" },
  { type: "item", id: CMD.FILE_SAVEALL, label: "Save All" },
  { type: "separator" },
  { type: "item", id: CMD.FILE_PRINT, label: "Print…", shortcut: "Ctrl+P" },
  { type: "item", id: CMD.EXPORT_TO_PDF, label: "Export to PDF…" },
  { type: "item", id: CMD.EXPORT_MAX_RENDERED_MD, label: "Export Max rendered Markdown…" },
  { type: "separator" },
  { type: "item", id: CMD.FILE_COPY_PATH, label: "Copy path / title" },
  { type: "item", id: CMD.FILE_SHARE_NULLAPAD, label: "Share to NullaPad…" },
];

const FILE_MENU_AFTER_RECENT: MenuEntry[] = [
  { type: "separator" },
  { type: "item", id: CMD.FILE_EXIT, label: "Exit" },
];

/** Label for MRU row (basename, truncated). */
export function formatRecentMenuLabel(path: string): string {
  const s = path.trim();
  if (!s) return "(unknown)";
  const slash = Math.max(s.lastIndexOf("/"), s.lastIndexOf("\\"));
  const base = slash >= 0 ? s.slice(slash + 1) : s;
  return base.length > 56 ? `${base.slice(0, 53)}…` : base;
}

export function buildRecentFilesSubmenuEntries(recent: readonly string[]): MenuEntry[] {
  const cap = Math.min(recent.length, MAX_RECENT_FILES, FILE_RECENT_SLOT_IDS.length);
  const out: MenuEntry[] = [];
  if (cap === 0) {
    out.push({ type: "item", id: CMD.FILE_RECENT_EMPTY, label: "(Empty)", enabled: false });
  } else {
    for (let i = 0; i < cap; i++) {
      out.push({
        type: "item",
        id: FILE_RECENT_SLOT_IDS[i]!,
        label: formatRecentMenuLabel(recent[i]!),
      });
    }
  }
  out.push(
    { type: "separator" },
    { type: "item", id: CMD.FILE_RECENT_CLEAR, label: "Clear recent files list" },
    { type: "separator" },
    { type: "item", id: CMD.FILE_RECENTFILES, label: "Show full paths…" },
  );
  return out;
}

function mapMenuEntriesWithShortcuts(
  entries: readonly MenuEntry[],
  shortcutOverrides: Readonly<Partial<Record<string, string>>>,
): MenuEntry[] {
  return entries.map((e) => {
    if (e.type === "item" && e.shortcut && shortcutOverrides[e.id] !== undefined) {
      return { ...e, shortcut: shortcutOverrides[e.id]! };
    }
    if (e.type === "submenu") {
      return { ...e, children: mapMenuEntriesWithShortcuts(e.children, shortcutOverrides) };
    }
    return e;
  });
}

function applyMenuShortcutOverrides(
  menus: readonly TopLevelMenu[],
  shortcutOverrides?: Readonly<Partial<Record<string, string>>>,
): readonly TopLevelMenu[] {
  if (!shortcutOverrides || Object.keys(shortcutOverrides).length === 0) {
    return menus;
  }
  return menus.map((top) => ({
    ...top,
    entries: mapMenuEntriesWithShortcuts(top.entries, shortcutOverrides),
  }));
}

export function buildNotepadMenuModel(
  recentPaths?: readonly string[],
  shortcutOverrides?: Readonly<Partial<Record<string, string>>>,
): readonly TopLevelMenu[] {
  const recent = recentPaths ?? DocumentManager.readRecent();
  const fileMenu: TopLevelMenu = {
    label: "File",
    entries: [
      ...FILE_MENU_BEFORE_RECENT,
      {
        type: "submenu",
        label: "Recent Files",
        children: buildRecentFilesSubmenuEntries(recent),
      },
      ...FILE_MENU_AFTER_RECENT,
    ],
  };
  return applyMenuShortcutOverrides([fileMenu, ...NOTEPAD_MENU_OTHER_TOP_LEVEL], shortcutOverrides);
}

const NOTEPAD_MENU_OTHER_TOP_LEVEL: readonly TopLevelMenu[] = [
  {
    label: "Edit",
    entries: [
      { type: "item", id: CMD.EDIT_UNDO, label: "Undo", shortcut: "Ctrl+Z" },
      { type: "item", id: CMD.EDIT_REDO, label: "Redo", shortcut: "Ctrl+Y" },
      { type: "separator" },
      { type: "item", id: CMD.EDIT_CUT, label: "Cut", shortcut: "Ctrl+X" },
      { type: "item", id: CMD.EDIT_COPY, label: "Copy", shortcut: "Ctrl+C" },
      { type: "item", id: CMD.EDIT_PASTE, label: "Paste", shortcut: "Ctrl+V" },
      { type: "item", id: CMD.EDIT_DELETE, label: "Delete", shortcut: "Del" },
      { type: "separator" },
      { type: "item", id: CMD.EDIT_SELECTALL, label: "Select All", shortcut: "Ctrl+A" },
      { type: "separator" },
      { type: "item", id: CMD.EDIT_UPPERCASE, label: "UPPERCASE" },
      { type: "item", id: CMD.EDIT_LOWERCASE, label: "lowercase" },
      { type: "item", id: CMD.EDIT_DUPLICATE_LINE, label: "Duplicate Current Line" },
      { type: "item", id: CMD.EDIT_TRIM_TRAILING, label: "Trim Trailing Space" },
      { type: "item", id: CMD.EDIT_INSERT_DATETIME, label: "Date Time (short)" },
      { type: "separator" },
      { type: "item", id: CMD.EDIT_LINE_UP, label: "Move Up Current Line", shortcut: "Ctrl+Shift+Up" },
      { type: "item", id: CMD.EDIT_LINE_DOWN, label: "Move Down Current Line", shortcut: "Ctrl+Shift+Down" },
      { type: "separator" },
      { type: "item", id: CMD.EDIT_JOIN_LINES, label: "Join Lines" },
      { type: "item", id: CMD.EDIT_REMOVE_EMPTY_LINES, label: "Remove Empty Lines" },
      { type: "item", id: CMD.EDIT_SORT_LINES, label: "Sort Lines (selection or all)" },
      { type: "separator" },
      { type: "item", id: CMD.EDIT_INDENT_INCREASE, label: "Indent (add 4 spaces)" },
      { type: "item", id: CMD.EDIT_INDENT_DECREASE, label: "Unindent (tab or 4 spaces)" },
    ],
  },
  {
    label: "Search",
    entries: [
      { type: "item", id: CMD.SEARCH_FIND, label: "Find…", shortcut: "Ctrl+F" },
      {
        type: "item",
        id: CMD.SEARCH_FINDINFILES,
        label: "Find in Files…",
        shortcut: "Ctrl+Shift+F",
      },
      {
        type: "item",
        id: CMD.SEARCH_FINDINDIRECTORY,
        label: "Find in Directory…",
      },
      { type: "separator" },
      { type: "item", id: CMD.SEARCH_FINDNEXT, label: "Find Next", shortcut: "F3" },
      { type: "item", id: CMD.SEARCH_FINDPREV, label: "Find Previous", shortcut: "Shift+F3" },
      { type: "item", id: CMD.SEARCH_SETANDFINDNEXT, label: "Select and Find Next", shortcut: "Ctrl+F3" },
      { type: "item", id: CMD.SEARCH_SETANDFINDPREV, label: "Select and Find Previous", shortcut: "Ctrl+Shift+F3" },
      { type: "item", id: CMD.SEARCH_VOLATILE_FINDNEXT, label: "Volatile Find Next", shortcut: "Ctrl+Alt+F3" },
      { type: "item", id: CMD.SEARCH_VOLATILE_FINDPREV, label: "Volatile Find Previous", shortcut: "Ctrl+Alt+Shift+F3" },
      { type: "separator" },
      { type: "item", id: CMD.SEARCH_FINDINCREMENT, label: "Incremental Search…", shortcut: "Ctrl+Alt+I" },
      { type: "separator" },
      { type: "item", id: CMD.SEARCH_REPLACE, label: "Replace…", shortcut: "Ctrl+H" },
      { type: "separator" },
      { type: "item", id: CMD.SEARCH_GOTOLINE, label: "Go to…", shortcut: "Ctrl+G" },
      { type: "item", id: CMD.SEARCH_GOTOMATCHINGBRACE, label: "Go to Matching Brace", shortcut: "Ctrl+B" },
      { type: "separator" },
      { type: "item", id: CMD.SEARCH_COUNT, label: "Count…" },
    ],
  },
  {
    label: "View",
    entries: [
      {
        type: "item",
        id: CMD.VIEW_WRAP,
        label: "Word wrap",
        checkbox: true,
        checked: false,
      },
      {
        type: "item",
        id: CMD.VIEW_LINENUMBER,
        label: "Line Number",
        checkbox: true,
        checked: true,
      },
      {
        type: "item",
        id: CMD.VIEW_MULTIVIEW,
        label: "Multiview (split editors)",
        shortcut: "Ctrl+Alt+M",
        checkbox: true,
        checked: false,
      },
      { type: "item", id: CMD.VIEW_SINGLE_EDITOR, label: "Single editor view" },
      { type: "item", id: CMD.VIEW_GIT_TERMINAL, label: "Git terminal" },
      { type: "separator" },
      { type: "item", id: CMD.WINDOW_TAB_MOVE_LEFT, label: "Move tab left", shortcut: "Ctrl+Shift+PageUp" },
      { type: "item", id: CMD.WINDOW_TAB_MOVE_RIGHT, label: "Move tab right", shortcut: "Ctrl+Shift+PageDown" },
      { type: "separator" },
      { type: "item", id: CMD.VIEW_ZOOMIN, label: "Zoom In", shortcut: "Ctrl++" },
      { type: "item", id: CMD.VIEW_ZOOMOUT, label: "Zoom Out", shortcut: "Ctrl+-" },
      { type: "item", id: CMD.VIEW_ZOOMRESTORE, label: "Restore Default Zoom", shortcut: "Ctrl+/" },
      { type: "separator" },
      { type: "item", id: CMD.VIEW_FULLSCREEN, label: "Toggle Full Screen Mode", shortcut: "F11" },
      { type: "separator" },
      {
        type: "submenu",
        label: "Examples",
        children: [
          { type: "item", id: CMD.EXAMPLE_QUICK_START, label: "Quick Start (MAX)" },
          { type: "item", id: CMD.EXAMPLE_SHOWCASE, label: "Showcase (MAX)" },
          { type: "item", id: CMD.EXAMPLE_MATH_PAPER, label: "Math Paper (MAX)" },
        ],
      },
    ],
  },
  {
    label: "Encoding",
    entries: [
      {
        type: "submenu",
        label: "Character sets",
        children: [
          { type: "item", id: CMD.FORMAT_UTF_8, label: "UTF-8" },
          { type: "item", id: CMD.FORMAT_ANSI, label: "ANSI" },
        ],
      },
      { type: "separator" },
      {
        type: "submenu",
        label: "EOL Conversion",
        children: [
          { type: "item", id: CMD.FORMAT_TODOS, label: "Windows (CR LF)" },
          { type: "item", id: CMD.FORMAT_TOUNIX, label: "Unix (LF)" },
          { type: "item", id: CMD.FORMAT_TOMAC, label: "Macintosh (CR)" },
        ],
      },
    ],
  },
  {
    label: "Language",
    entries: [
      ...LANGUAGE_MENU_ROWS.map((row) =>
        row.kind === "separator"
          ? { type: "separator" as const }
          : { type: "item" as const, id: row.id, label: languageLabel(row.mode) },
      ),
      { type: "separator" },
      { type: "item", id: CMD.LANG_STYLE_DLG, label: "Style Configurator…" },
    ],
  },
  {
    label: "Settings",
    entries: [
      { type: "item", id: CMD.SETTING_PREF, label: "Preferences…" },
      { type: "item", id: CMD.SETTING_MARKDOWN_PREVIEW_STYLE, label: "Markdown preview style…" },
      { type: "item", id: CMD.SETTING_SHORTCUT, label: "Shortcut Mapper…" },
    ],
  },
  {
    label: "Tools",
    entries: [
      { type: "item", id: CMD.TOOL_MD5, label: "MD5 → Generate…" },
      { type: "item", id: CMD.TOOL_SHA256, label: "SHA-256 → Generate…" },
      { type: "separator" },
      { type: "item", id: CMD.TOOL_BASE64_ENCODE, label: "Base64 → Encode selection" },
      { type: "item", id: CMD.TOOL_BASE64_DECODE, label: "Base64 → Decode selection" },
      { type: "separator" },
      {
        type: "item",
        id: CMD.TOOL_GEMINI_BEAUTIFY_SELECTION,
        label: "Gemini: review & beautify selection…",
      },
    ],
  },
  {
    label: "?",
    entries: [
      { type: "item", id: CMD.HELP_NULLAPAD_IDENTITY, label: "Focus instance secret (title bar)…" },
      { type: "separator" },
      { type: "item", id: CMD.HELP_CMDLINE, label: "Command Line Arguments…" },
      {
        type: "submenu",
        label: "About",
        children: [
          { type: "item", id: CMD.HELP_NULLAPAD_STORY, label: "About NullaPad…" },
          { type: "item", id: CMD.HELP_NULLAPAD_USERS_GUIDE, label: "NullaPad Users Guide…" },
          { type: "item", id: CMD.HELP_NULLAPAD_MAX_GUIDE, label: "MAX Language Guide…" },
          { type: "item", id: CMD.HELP_ABOUT, label: "Debug Info…" },
        ],
      },
    ],
  },
] satisfies readonly TopLevelMenu[];

/** Snapshot with current MRU (for tests / static analysis); use `buildNotepadMenuModel()` when refreshing UI. */
export const NOTEPAD_MENU_MODEL: readonly TopLevelMenu[] = buildNotepadMenuModel([]);

export function getTopLevelLabels(model: readonly TopLevelMenu[]): string[] {
  return model.map((m) => m.label);
}

/** Flatten all command IDs in traversal order (depth-first). */
export function collectCommandIds(entries: readonly MenuEntry[]): string[] {
  const out: string[] = [];
  const walk = (list: readonly MenuEntry[]) => {
    for (const e of list) {
      if (e.type === "item") out.push(e.id);
      else if (e.type === "submenu") walk(e.children);
    }
  };
  walk(entries);
  return out;
}

export function allMenuCommandIds(): string[] {
  const ids: string[] = [];
  const sampleRecent = Array.from({ length: MAX_RECENT_FILES }, (_, i) => `recent-${i}`);
  for (const top of buildNotepadMenuModel(sampleRecent)) ids.push(...collectCommandIds(top.entries));
  return [...new Set(ids)];
}
